// One component per recurring badge concept so the same idea always looks the same: revisions,
// org roles, counts, pilot identity, and viewer metadata. Labels are Title Case (casing policy).
import type { ComponentProps, ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";

import { Badge, type BadgeProps } from "@/components/ui/badge";
import { roleLabel } from "@/features/organization-members/member-change-copy";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------------------------------
 * RevisionBadge
 * -----------------------------------------------------------------------------------------------*/

export interface RevisionBadgeProps {
  number: number;
  /** Appends "· Current" — the revision the document currently points at. */
  current?: boolean;
  /** Appends "· Latest" — the newest revision when "current" is not the right word. */
  latest?: boolean;
  size?: BadgeProps["size"];
  className?: string;
}

/** "R3", "R3 · Current", or "R3 · Latest". */
export function RevisionBadge({
  number,
  current = false,
  latest = false,
  size = "sm",
  className,
}: RevisionBadgeProps) {
  const suffix = current ? "Current" : latest ? "Latest" : null;
  return (
    <Badge
      variant="outline"
      size={size}
      data-slot="revision-badge"
      className={cn("tabular-nums", suffix && "border-primary/50", className)}
    >
      {`R${number}`}
      {suffix ? (
        <>
          <span aria-hidden="true" className="text-muted-foreground">
            ·
          </span>
          {suffix}
        </>
      ) : null}
    </Badge>
  );
}

/* -------------------------------------------------------------------------------------------------
 * RoleBadge
 * -----------------------------------------------------------------------------------------------*/

export interface RoleBadgeProps {
  /** Org role slug (`owner` | `admin` | `member` | `reviewer`); labels come from `ROLE_LABELS`. */
  role: string | null | undefined;
  className?: string;
}

export function RoleBadge({ role, className }: RoleBadgeProps) {
  return (
    <Badge
      variant="outline"
      size="sm"
      data-slot="role-badge"
      className={cn(role === "reviewer" && "bg-muted/50 text-muted-foreground", className)}
    >
      {roleLabel(role)}
    </Badge>
  );
}

/* -------------------------------------------------------------------------------------------------
 * CountBadge
 * -----------------------------------------------------------------------------------------------*/

export type CountBadgeTone = "muted" | "onPrimary" | "primary";

const COUNT_TONE_CLASSES: Record<CountBadgeTone, string> = {
  muted: "border-transparent bg-muted text-muted-foreground",
  onPrimary: "border-transparent bg-primary-foreground/20 text-current",
  primary: "border-transparent bg-primary text-primary-foreground",
};

export interface CountBadgeProps {
  value: number;
  /**
   * Screen-reader description read after the number ("unread notifications" → "3 unread
   * notifications"). Omit when adjacent text already explains the count.
   */
  label?: string;
  /**
   * `muted` (default) for neutral counts, `onPrimary` when the badge sits on a primary surface
   * (active tab/button), `primary` for an attention count on neutral chrome (Inbox).
   */
  tone?: CountBadgeTone;
  /** Values above this render as "{max}+". */
  max?: number;
  className?: string;
}

/** Numeric pill. Counts never use status colors. */
export function CountBadge({ value, label, tone = "muted", max = 99, className }: CountBadgeProps) {
  const display = value > max ? `${max}+` : String(value);
  return (
    <Badge
      size="sm"
      data-slot="count-badge"
      className={cn(
        "min-w-5 rounded-full px-1.5 tabular-nums",
        COUNT_TONE_CLASSES[tone],
        className
      )}
    >
      {display}
      {label ? <span className="sr-only"> {label}</span> : null}
    </Badge>
  );
}

/* -------------------------------------------------------------------------------------------------
 * PilotChip
 * -----------------------------------------------------------------------------------------------*/

export type PilotKey = "spec" | "takeoff" | "field" | "rev" | "collab";

export const PILOT_LABELS: Record<PilotKey, string> = {
  spec: "SpecPilot",
  takeoff: "TakeoffPilot",
  field: "FieldPilot",
  rev: "RevPilot",
  collab: "CollabPilot",
};

// Full literal class strings so Tailwind can see every `--pilot-*` utility.
const PILOT_CHIP_CLASSES: Record<PilotKey, string> = {
  spec: "border-pilot-spec/50 bg-pilot-spec/10 text-pilot-spec [a&]:hover:bg-pilot-spec/20 [button&]:hover:bg-pilot-spec/20",
  takeoff:
    "border-pilot-takeoff/50 bg-pilot-takeoff/10 text-pilot-takeoff [a&]:hover:bg-pilot-takeoff/20 [button&]:hover:bg-pilot-takeoff/20",
  field:
    "border-pilot-field/50 bg-pilot-field/10 text-pilot-field [a&]:hover:bg-pilot-field/20 [button&]:hover:bg-pilot-field/20",
  rev: "border-pilot-rev/50 bg-pilot-rev/10 text-pilot-rev [a&]:hover:bg-pilot-rev/20 [button&]:hover:bg-pilot-rev/20",
  collab:
    "border-pilot-collab/50 bg-pilot-collab/10 text-pilot-collab [a&]:hover:bg-pilot-collab/20 [button&]:hover:bg-pilot-collab/20",
};

export interface PilotChipProps extends Omit<ComponentProps<"span">, "children"> {
  pilot: PilotKey;
  /** Defaults to the pilot name ("SpecPilot"). With `asChild`, pass the link element instead. */
  children?: ReactNode;
  /** Render the single child (e.g. an `AppLink`) with chip styling. */
  asChild?: boolean;
}

/** Pilot identity pill tinted with the theme's `--pilot-*` hue. */
export function PilotChip({ pilot, children, asChild = false, className, ...props }: PilotChipProps) {
  const classes = cn(
    "inline-flex h-6 w-fit shrink-0 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
    PILOT_CHIP_CLASSES[pilot],
    className
  );
  if (asChild) {
    return (
      <Slot data-slot="pilot-chip" data-pilot={pilot} className={classes} {...props}>
        {children}
      </Slot>
    );
  }
  return (
    <span data-slot="pilot-chip" data-pilot={pilot} className={classes} {...props}>
      {children ?? PILOT_LABELS[pilot]}
    </span>
  );
}

/* -------------------------------------------------------------------------------------------------
 * ViewerMetaChip
 * -----------------------------------------------------------------------------------------------*/

export type ViewerMetaChipProps = ComponentProps<"span">;

/** Muted, normal-case metadata pill for viewer bars ("Sheet A-101", "12 measurements"). */
export function ViewerMetaChip({ className, ...props }: ViewerMetaChipProps) {
  return (
    <Badge
      variant="outline"
      size="sm"
      data-slot="viewer-meta-chip"
      className={cn("bg-muted/50 font-medium text-muted-foreground", className)}
      {...props}
    />
  );
}

/* -------------------------------------------------------------------------------------------------
 * DeltaBadge
 * -----------------------------------------------------------------------------------------------*/

export interface DeltaBadgeProps {
  /** Signed change; picks the tone (0 neutral, positive primary, negative destructive). */
  value: number;
  /** Defaults to the signed number ("+3", "-2", "0"); pass formatted text for units. */
  children?: ReactNode;
  className?: string;
}

/** Quantity change between two revisions. */
export function DeltaBadge({ value, children, className }: DeltaBadgeProps) {
  const variant: BadgeProps["variant"] =
    value === 0 ? "outline" : value > 0 ? "default" : "destructive";
  return (
    <Badge
      variant={variant}
      size="sm"
      data-slot="delta-badge"
      className={cn("tabular-nums", className)}
    >
      {children ?? (value > 0 ? `+${value}` : String(value))}
    </Badge>
  );
}

/* -------------------------------------------------------------------------------------------------
 * SubmittalSelectionBadge
 * -----------------------------------------------------------------------------------------------*/

export interface SubmittalSelectionBadgeProps {
  selected: boolean;
  className?: string;
}

const SELECTION_VARIANT: Record<"true" | "false", BadgeProps["variant"]> = {
  true: "secondary",
  false: "outline",
};

/** Whether a SpecPilot table is included in the submittal. */
export function SubmittalSelectionBadge({ selected, className }: SubmittalSelectionBadgeProps) {
  return (
    <Badge
      variant={SELECTION_VARIANT[String(selected) as "true" | "false"]}
      size="sm"
      data-slot="submittal-selection-badge"
      className={className}
    >
      {selected ? "In Submittal" : "Not In Submittal"}
    </Badge>
  );
}
